import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle, Play, Lock, Download, Clock, ArrowRight } from 'lucide-react';
import { Footer } from './Footer';
import { TeamMembers } from './TeamMembers';
import { modules } from '../data/modules';
import { apiUrl } from '../config/api';

interface User {
  id: string;
  email: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  company: string;
  role: string;
  status: string;
  userType?: string;
  lastActivityAt?: string;
  lastInactivityEmailAt?: string;
}

interface DashboardProps {
  user: User;
  onLogout: () => void;
}

interface UserProgress {
  userId: string;
  userEmail: string;
  userName: string;
  role: string;
  completedModuleIds: number[];
  completedModules: Array<{
    moduleId: number;
    completed: boolean;
    completedAt: string;
  }>;
  modules: Array<{
    moduleId: number;
    completed: boolean;
  }>;
}

export function Dashboard({ user, onLogout }: DashboardProps) {
  const [userProgress, setUserProgress] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastVisitedModuleId, setLastVisitedModuleId] = useState<string | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    return localStorage.getItem('lastVisitedModuleId');
  });
  const location = useLocation();

  const getModuleLink = (moduleId: number) => {
    switch (moduleId) {
      case 1:
        return `/user/${user.id}/module1`;
      case 2:
        return `/user/${user.id}/module2`;
      case 3:
        return `/user/${user.id}/module3`;
      case 4:
        return `/user/${user.id}/module4`;
      case 5:
        return `/user/${user.id}/module5`;
      case 6:
        return `/user/${user.id}/module6`;
      case 7:
        return `/user/${user.id}/module7`;
      case 8:
        return `/user/${user.id}/module8`;
      default:
        return `/user/${user.id}/module/${moduleId}`;
    }
  };

  // Fetch user progress from backend
  const fetchUserProgress = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      const response = await fetch(apiUrl('/api/user-progress'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      // Handle non-200 responses
      if (!response.ok) {
        if (response.status === 404) {
          console.warn('⚠️ User progress API not found (404). Using empty progress.');
          // Set empty progress so modules still display
          setUserProgress({
            userId: user.id,
            userEmail: user.email,
            userName: user.fullName,
            role: user.role,
            completedModuleIds: [],
            completedModules: [],
            modules: []
          });
          setError(null);
          return;
        }
        
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch progress' }));
        setError(errorData.message || 'Failed to fetch progress');
        // Still set empty progress for admins so modules display
        if (user.role === 'admin') {
          setUserProgress({
            userId: user.id,
            userEmail: user.email,
            userName: user.fullName,
            role: user.role,
            completedModuleIds: [],
            completedModules: [],
            modules: []
          });
        }
        return;
      }

      const data = await response.json();

      if (data.success) {
        console.log('📊 User progress fetched:', data.data);
        console.log('✅ Completed module IDs:', data.data.completedModuleIds);
        setUserProgress(data.data);
        setError(null);
      } else {
        setError(data.message || 'Failed to fetch progress');
        // Still set empty progress for admins so modules display
        if (user.role === 'admin') {
          setUserProgress({
            userId: user.id,
            userEmail: user.email,
            userName: user.fullName,
            role: user.role,
            completedModuleIds: [],
            completedModules: [],
            modules: []
          });
        }
      }
    } catch (err) {
      console.error('Error fetching user progress:', err);
      setError('Failed to load progress. Please refresh the page.');
      // Still set empty progress for admins so modules display
      if (user.role === 'admin') {
        setUserProgress({
          userId: user.id,
          userEmail: user.email,
          userName: user.fullName,
          role: user.role,
          completedModuleIds: [],
          completedModules: [],
          modules: []
        });
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch progress on mount and when location changes (user navigates to dashboard)
  useEffect(() => {
    fetchUserProgress();
  }, [location.pathname, fetchUserProgress]); // Refresh when route changes (e.g., user returns to dashboard)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const storedModuleId = localStorage.getItem('lastVisitedModuleId');
    if (storedModuleId) {
      setLastVisitedModuleId(storedModuleId);
    }
  }, []);

  // Check if user is admin
  const isAdmin = user.role === 'admin';

  // Map modules with progress and locking logic
  // First pass: Get completion status for all modules
  const modulesWithCompletion = modules.map((module) => {
    const completedModuleIds = userProgress?.completedModuleIds || [];
    const isCompleted = completedModuleIds.includes(Number(module.id));
    console.log(`📋 Module ${module.id}: completed=${isCompleted}, completedModuleIds=`, completedModuleIds);
    return {
      ...module,
      completed: isCompleted
    };
  });

  // Second pass: Calculate accessibility based on previous module completion
  const dashboardModules = modulesWithCompletion.map((module, index) => {
    const isCompleted = module.completed;
    
    // Admin bypass: All modules accessible
    // Regular users: Module 1 always accessible, Module N+1 only if Module N is completed
    let isAccessible = false;
    if (isAdmin) {
      // Admin sees all modules unlocked
      isAccessible = true;
    } else if (index === 0) {
      // Module 1 is always accessible
      isAccessible = true;
    } else {
      // Module N+1 is accessible only if previous module is completed
      const previousModule = modulesWithCompletion[index - 1];
      isAccessible = previousModule.completed;
    }
    
    return {
      ...module,
      progress: isCompleted ? 100 : 0,
      accessible: isAccessible
    };
  });

  const completedModules = dashboardModules.filter(m => m.completed).length;
  const totalProgress = Math.round((completedModules / dashboardModules.length) * 100);
  const hasCompletedModules = (userProgress?.completedModuleIds?.length || 0) > 0;
  const hasModuleCompletions = userProgress?.modules?.some(module => module.completed) || false;
  const hasLocalStart = Boolean(lastVisitedModuleId);
  const hasStartedCourse = hasCompletedModules || hasModuleCompletions || hasLocalStart;
  const allModulesCompleted = dashboardModules.length > 0 && dashboardModules.every(module => module.completed);
  const firstAccessibleIncompleteModule = dashboardModules.find(module => module.accessible && !module.completed);
  const firstAccessibleModule = dashboardModules.find(module => module.accessible) || dashboardModules[0];
  const ctaModule = allModulesCompleted
    ? dashboardModules[0]
    : firstAccessibleIncompleteModule || firstAccessibleModule || dashboardModules[0];
  const ctaPath = ctaModule ? getModuleLink(ctaModule.id) : `/user/${user.id}/module1`;
  const ctaLabel = allModulesCompleted
    ? 'Review Course'
    : hasStartedCourse
      ? 'Continue Learning'
      : 'Start Learning';
  const inactivityThresholdDays = 4;
  const inactivityThresholdMs = inactivityThresholdDays * 24 * 60 * 60 * 1000;
  const lastActivityDate = user.lastActivityAt ? new Date(user.lastActivityAt) : null;
  const now = new Date();
  const msSinceLastActivity = lastActivityDate ? now.getTime() - lastActivityDate.getTime() : null;
  const daysSinceLastActivity = msSinceLastActivity !== null ? Math.max(0, Math.floor(msSinceLastActivity / (1000 * 60 * 60 * 24))) : null;
  const showInactivityReminder = !isAdmin && !!lastActivityDate && msSinceLastActivity !== null && msSinceLastActivity >= inactivityThresholdMs;
  const inactivityDayCount = daysSinceLastActivity ?? inactivityThresholdDays;
  const lastReminderDate = user.lastInactivityEmailAt ? new Date(user.lastInactivityEmailAt) : null;

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'var(--white)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="container" style={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
          {/* Welcome Section */}
          <section style={{ margin: '3rem 0' }}>
            <div style={{ 
              background: 'linear-gradient(135deg, var(--dark) 0%, var(--dark-blue) 100%)', 
              borderRadius: '12px', 
              padding: '2rem', 
              color: 'white',
              marginBottom: '2rem'
            }}>
              <h1 style={{ 
                fontSize: '2.5rem', 
                fontWeight: 600, 
                marginBottom: '1rem', 
                fontFamily: 'var(--montserrat)' 
              }}>
                Welcome back, {user.firstName || (user.fullName ? user.fullName.split(/\s+/)[0] : user.email.split('@')[0])}!
              </h1>
              <p style={{ 
                fontSize: '1.125rem', 
                color: 'rgba(255, 255, 255, 0.9)', 
                marginBottom: '2rem' 
              }}>
                Continue your journey to master Principal IT Talent sourcing techniques.
              </p>
              {!isAdmin && (
                <div style={{ marginBottom: '2.5rem' }}>
                  <Link
                    to={ctaPath}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: 'var(--orange)',
                      color: 'white',
                      padding: '0.85rem 1.75rem',
                      borderRadius: '999px',
                      fontWeight: 600,
                      fontSize: '1rem',
                      textDecoration: 'none',
                      boxShadow: '0 10px 20px rgba(0,0,0,0.25)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      pointerEvents: loading ? 'none' : 'auto',
                      opacity: loading ? 0.7 : 1
                    }}
                    onClick={() => {
                      if (!ctaModule) {
                        return;
                      }
                      localStorage.setItem('lastVisitedModuleId', String(ctaModule.id));
                      setLastVisitedModuleId(String(ctaModule.id));
                    }}
                  >
                    {ctaLabel}
                    {hasStartedCourse || allModulesCompleted ? <ArrowRight size={18} /> : <Play size={18} />}
                  </Link>
                </div>
              )}
              {showInactivityReminder && (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginBottom: '2rem',
                  backdropFilter: 'blur(4px)'
                }}>
                  <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1.25rem', fontWeight: 600, color: 'white' }}>
                    Welcome back! 👋
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
                    It looks like it's been {inactivityDayCount} {inactivityDayCount === 1 ? 'day' : 'days'} since your last session.
                    Your next module is ready—jump back in when you're ready.
                  </p>
                  {lastReminderDate && (
                    <p style={{ margin: '0.75rem 0 0 0', color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem' }}>
                      Reminder sent on {lastReminderDate.toLocaleDateString()}.
                    </p>
                  )}
                </div>
              )}
              
              <div className="admin-progress-metrics">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--orange)', marginBottom: '0.5rem' }}>
                    {totalProgress}%
                  </div>
                  <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Course Progress</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--orange)', marginBottom: '0.5rem' }}>
                    {completedModules}/{modules.length}
                  </div>
                  <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Modules Completed</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--orange)', marginBottom: '0.5rem' }}>
                    {completedModules * 30}min
                  </div>
                  <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Time Invested</div>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div style={{ 
              background: 'white', 
              border: '1px solid #eee', 
              borderRadius: '12px', 
              padding: '1.5rem', 
              marginBottom: '2rem' 
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
                  Overall Progress
                </h3>
                <span style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--orange)' }}>
                  {totalProgress}%
                </span>
              </div>
              <div style={{ 
                height: '8px', 
                background: '#eee', 
                borderRadius: '999px', 
                overflow: 'hidden' 
              }}>
                <div style={{ 
                  height: '100%', 
                  background: 'linear-gradient(90deg, var(--orange) 0%, #E6C85C 100%)', 
                  borderRadius: '999px', 
                  width: `${totalProgress}%`,
                  transition: 'width 0.5s ease'
                }} />
              </div>
            </div>
          </section>

          {/* Course Modules */}
          <section>
            <h2 style={{ 
              fontSize: '2rem', 
              fontWeight: 600, 
              color: 'var(--dark)', 
              marginBottom: '2rem',
              fontFamily: 'var(--montserrat)'
            }}>
              Course Modules
            </h2>
            
            <div className="admin-modules-grid">
              {loading ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem' }}>
                  <p style={{ color: 'var(--gray)', fontSize: '1rem' }}>Loading your progress...</p>
                </div>
              ) : error ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem' }}>
                  <p style={{ color: '#ef4444', fontSize: '1rem' }}>{error}</p>
                </div>
              ) : (
                dashboardModules.map((module, index) => {
                  const isLocked = !module.accessible;
                
                return (
                  <div 
                    key={module.id}
                    style={{ 
                      background: 'white', 
                      borderRadius: '12px', 
                      overflow: 'hidden', 
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      border: module.completed ? '2px solid var(--orange)' : '1px solid #e5e7eb',
                      cursor: isLocked ? 'not-allowed' : 'pointer',
                      opacity: isLocked ? 0.6 : 1,
                      transition: 'all 0.2s ease',
                      display: 'flex', 
                      flexDirection: 'column', 
                      height: '100%'
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                    <img 
                      src={module.image} 
                      alt={module.title} 
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover'
                        }}
                      />
                      {module.completed && (
                        <div style={{ 
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                            background: 'var(--orange)', 
                            color: 'white', 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px', 
                            fontSize: '0.75rem', 
                          fontWeight: 600,
                            textTransform: 'uppercase' 
                          }}>
                            COMPLETED
                        </div>
                        )}
                        {isLocked && (
                        <div style={{
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                            background: 'var(--gray)', 
                            color: 'white', 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px', 
                            fontSize: '0.75rem', 
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}>
                          <Lock size={12} />
                          LOCKED
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.5rem',
                        fontSize: '0.875rem',
                        color: 'var(--orange)',
                        fontWeight: '500'
                      }}>
                        <Clock size={14} />
                        <span>{module.duration}</span>
                        <span>•</span>
                        <span>{module.type}</span>
                      </div>
                      
                      <h2 style={{ 
                        fontSize: '1.25rem', 
                        fontWeight: '600', 
                        color: 'var(--dark)', 
                        marginBottom: '1rem',
                        lineHeight: '1.3'
                      }}>
                        {(() => {
                          const titleMatch = module.title.match(/^Module (\d+) (.+)$/);
                          if (titleMatch) {
                            return (
                              <>
                                <span>Module </span>
                                <span style={{ color: 'var(--orange)' }}>{titleMatch[1]}</span>
                                <span> {titleMatch[2]}</span>
                              </>
                            );
                          }
                          return module.title;
                        })()}
                      </h2>
                      
                          <p style={{ 
                            color: 'var(--gray)', 
                        marginBottom: '1rem', 
                        lineHeight: '1.5',
                        flex: 1
                      }}>
                        {module.description}
                      </p>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <h4 style={{ 
                          fontSize: '0.875rem', 
                          fontWeight: '600', 
                          color: 'var(--dark)', 
                          marginBottom: '0.5rem'
                        }}>
                          What you'll learn:
                        </h4>
                        <ul style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0
                        }}>
                          {module.whatYoullLearn.map((topic, idx) => (
                            <li key={idx} style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '0.5rem',
                              marginBottom: '0.25rem',
                              fontSize: '0.875rem',
                              color: 'var(--gray)'
                            }}>
                              <CheckCircle size={14} color="var(--orange)" style={{ marginTop: '2px', flexShrink: 0 }} />
                              <span>{topic}</span>
                            </li>
                          ))}
                        </ul>
                        </div>
                      
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem', 
                        padding: '0.75rem',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}>
                        <Download size={16} color="var(--orange)" />
                        <span style={{ fontSize: '0.875rem', color: 'var(--dark)' }}>
                          {module.takeaway}
                        </span>
                      </div>
                      
                      {!isLocked ? (
                        <Link
                          to={getModuleLink(module.id)}
                          style={{ 
                            background: module.completed ? 'var(--orange)' : 'var(--dark)',
                            color: 'white',
                            padding: '0.75rem 1rem',
                            borderRadius: '8px',
                            textDecoration: 'none',
                            textAlign: 'center',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => {
                            localStorage.setItem('lastVisitedModuleId', String(module.id));
                            setLastVisitedModuleId(String(module.id));
                          }}
                        >
                          {module.completed ? (
                            <>
                              <CheckCircle size={16} />
                              Review Module
                            </>
                          ) : (
                            <>
                              <Play size={16} />
                              Start Learning
                            </>
                          )}
                        </Link>
                      ) : (
                        <div style={{ 
                            background: 'var(--gray)',
                          color: 'white', 
                          padding: '0.75rem 1rem', 
                          borderRadius: '8px', 
                          textAlign: 'center', 
                          fontSize: '0.875rem', 
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem'
                        }}>
                          <Lock size={16} />
                          Complete Previous Module
                        </div>
                      )}
                    </div>
                  </div>
                );
              }))}
            </div>
          </section>

          {/* Team Members Section */}
          <section style={{ marginTop: '3rem', marginBottom: '4rem' }}>
            <TeamMembers user={user} />
          </section>

        </div>

      <Footer />
    </div>
  );
}

export default Dashboard;